// CampaignsPage — sequenced outreach that never sends (BUILD-BRIEF-R2 §10, mockup "Campaigns").
//
// The instrument: five counts, then four zones — the segment persona a sequence addresses, the
// sequences themselves with their steps and enrolments, the drafts waiting for a human, and
// twelve months of attribution.
//
// THE THREE THINGS THIS PAGE MUST NOT DO:
//
//  · It must not send. There is no send verb here and there is none behind it: a step produces
//    a `draft` PROPOSAL, the operator holds it in Review, and the send is a separate click in
//    the Inbox. "Approve" on this page routes to Review; it does not approve.
//  · It must not print a reply rate from four sends. REPLY RATE reads `fv--unknown` "too few to
//    say" until twenty messages have gone out, and so does every variant row in Attribution.
//  · It must not hide a suppression. Every enrolment that was skipped carries the sentence that
//    says why, in the row and in the zone note.
//
// HONEST STATES: a failed poll leaves the rail at "not collected" (unknown), never at zero. A
// counted zero is `fv--nil` and looks different on purpose.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Att, Fig, Lnk, Mast, PageShell, Rail, RailCell, Record, Ref, Sep, Sheet, Tag, Tbl, Zone, ZoneHead,
} from '../components/PageKit.js';
import EmptyState from '../components/EmptyState.js';
import SegmentPersonaZone from '../components/SegmentPersonaZone.js';
import { useToast } from '../components/Toast.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import { ApiError } from '../api/client.js';
import {
  campaignsSummary, createSequence, enrol, listEnrolments, listSequences, resumeEnrolment,
  runTick, setSequenceStatus, stopEnrolment, validateSteps, type CampaignsSummary, type SequenceVerb,
} from '../api/sequences.js';
import { listExperiments, report as experimentReport, type AttributionReport } from '../api/experiments.js';
import { listPersons } from '../api/persons.js';
import type { Enrolment, Experiment, Person, Sequence, Step } from '../../shared/types.js';
import './CampaignsPage.de.js';
import './CampaignsPage.fr.js';

// ── the step editor's own little model ──────────────────────────────────────

interface DraftStep {
  wait_days: string;
  subject: string;
  template_md: string;
  /** "stop when they reply" is the DEFAULT and needs no branch; this is the opposite intent. */
  end_on_no_reply: boolean;
}

const emptyStep = (waitDays: number): DraftStep => ({
  wait_days: String(waitDays), subject: '', template_md: '', end_on_no_reply: false,
});

function toSteps(rows: DraftStep[]): Step[] {
  return rows.map((row, index) => ({
    n: index + 1,
    wait_days: Number(row.wait_days) || 0,
    channel: 'mail' as const,
    subject: row.subject.trim(),
    template_md: row.template_md.trim(),
    branches: row.end_on_no_reply ? [{ on: 'no_reply' as const, goto: 'end' as const }] : [],
  }));
}

const statusTone = (status: Sequence['status']): 'ok' | 'attend' | 'ghost' | 'neutral' =>
  status === 'running' ? 'ok' : status === 'paused' ? 'attend' : status === 'draft' ? 'ghost' : 'neutral';

const enrolmentTone = (status: Enrolment['status']): 'ok' | 'attend' | 'broken' | 'ghost' | 'neutral' =>
  status === 'active' ? 'ok'
    : status === 'replied' ? 'attend'
      : status === 'suppressed' ? 'broken'
        : status === 'paused' ? 'ghost' : 'neutral';

export default function CampaignsPage() {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtNum } = useLocale();
  const toast = useToast();

  const [summary, setSummary] = useState<CampaignsSummary | null>(null);
  const [sequences, setSequences] = useState<Sequence[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [enrolments, setEnrolments] = useState<Enrolment[] | null>(null);
  const [experiments, setExperiments] = useState<Experiment[] | null>(null);
  const [reports, setReports] = useState<Record<number, AttributionReport>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // the editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [name, setName] = useState('');
  const [dailyCap, setDailyCap] = useState('10');
  const [steps, setSteps] = useState<DraftStep[]>([emptyStep(0)]);
  const [stepError, setStepError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // the enrol box
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Person[] | null>(null);
  const [picked, setPicked] = useState<number[]>([]);

  const load = useCallback(async () => {
    const [s, q, x] = await Promise.allSettled([campaignsSummary(), listSequences(), listExperiments()]);
    // A failed poll is silence: the state stays null and the rail reads "not collected".
    setSummary(s.status === 'fulfilled' ? s.value : null);
    setSequences(q.status === 'fulfilled' ? q.value.items : null);
    setExperiments(x.status === 'fulfilled' ? x.value.items : null);
    setError(q.status === 'rejected' ? (q.reason instanceof Error ? q.reason.message : String(q.reason)) : null);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const selected = useMemo(
    () => (sequences ?? []).find((row) => row.id === selectedId) ?? (sequences ?? [])[0] ?? null,
    [sequences, selectedId],
  );

  const loadEnrolments = useCallback(async () => {
    if (!selected) { setEnrolments(null); return; }
    try {
      setEnrolments((await listEnrolments(selected.id)).items);
    } catch {
      setEnrolments(null);
    }
  }, [selected]);

  useEffect(() => { void loadEnrolments(); }, [loadEnrolments]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const out: Record<number, AttributionReport> = {};
      for (const experiment of experiments ?? []) {
        try { out[experiment.id] = await experimentReport(experiment.id); } catch { /* silence */ }
      }
      if (alive) setReports(out);
    })();
    return () => { alive = false; };
  }, [experiments]);

  // ── verbs ────────────────────────────────────────────────────────────────

  const changeStatus = useCallback(async (sequence: Sequence, verb: SequenceVerb) => {
    setBusy(`seq-${sequence.id}`);
    try {
      await setSequenceStatus(sequence.id, verb);
      toast('ok', t('{ref} is now {status}', { ref: sequence.ref, status: t(verb === 'end' ? 'ended' : verb === 'pause' ? 'paused' : 'running') }));
      await load();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not change the sequence.'));
    } finally {
      setBusy(null);
    }
  }, [load, t, toast]);

  const save = useCallback(async () => {
    setStepError(null);
    const built = toSteps(steps);
    setSaving(true);
    try {
      const check = await validateSteps(built);
      if (!check.ok) { setStepError(check.error ?? t('The steps could not be read.')); return; }
      const created = await createSequence({ name: name.trim(), steps: built, daily_cap: Number(dailyCap) || 10 });
      toast('ok', t('{ref} created as a draft. Enrol people, then start it.', { ref: created.ref }));
      setEditorOpen(false);
      setName('');
      setSteps([emptyStep(0)]);
      await load();
      setSelectedId(created.id);
    } catch (err) {
      setStepError(err instanceof ApiError ? err.message : t('Could not create the sequence.'));
    } finally {
      setSaving(false);
    }
  }, [dailyCap, load, name, steps, t, toast]);

  const search = useCallback(async () => {
    try {
      setMatches((await listPersons({ q: query.trim(), status: 'held', limit: 20 })).items);
    } catch {
      setMatches(null);
    }
  }, [query]);

  const doEnrol = useCallback(async () => {
    if (!selected || !picked.length) return;
    setBusy('enrol');
    try {
      const answer = await enrol(selected.id, picked);
      toast('ok', t('{n} enrolled in {ref}', { n: fmtNum(answer.total), ref: selected.ref }));
      setPicked([]);
      setEnrolOpen(false);
      await Promise.all([load(), loadEnrolments()]);
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not enrol.'));
    } finally {
      setBusy(null);
    }
  }, [fmtNum, load, loadEnrolments, picked, selected, t, toast]);

  const tickNow = useCallback(async () => {
    setBusy('tick');
    try {
      const answer = await runTick();
      toast(answer.status === 'ok' ? 'ok' : 'warn', t('Tick: {status}', { status: t(answer.status) })
        + (answer.error ? ` — ${answer.error}` : ''));
      await Promise.all([load(), loadEnrolments()]);
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('The tick could not be run.'));
    } finally {
      setBusy(null);
    }
  }, [load, loadEnrolments, t, toast]);

  // ── the rail ─────────────────────────────────────────────────────────────

  const reply = summary?.reply_12m ?? null;
  const topStep = summary?.drafts_by_step?.[0] ?? null;

  return (
    <PageShell>
      <Mast
        kicker={t('Outreach')}
        title={t('Campaigns')}
        sub={
          <>
            {t('segment personas feed sequences')}
            <Sep />{t('every draft is written from the person’s own record')}
            <Sep />{t('you approve each one')}
            {summary && (
              <>
                <Sep /><Fig>{fmtNum(summary.hourly.remaining)}</Fig> {t('of')} <Fig>{fmtNum(summary.hourly.cap)}</Fig> {t('left this hour')}
              </>
            )}
          </>
        }
        verbs={
          <>
            <button type="button" className="btn" disabled={busy === 'tick'} onClick={() => void tickNow()}>
              {busy === 'tick' ? t('Running…') : t('Run tick now')}
            </button>
            <button type="button" className="btn btn-accent" onClick={() => setEditorOpen((v) => !v)}>{t('New sequence')}</button>
          </>
        }
      />

      <Rail>
        <RailCell
          label={t('Personas')}
          value={summary ? fmtNum(summary.personas.total) : t('not collected')}
          tone={!summary ? 'unknown' : summary.personas.total === 0 ? 'nil' : 'ink'}
          detail={summary
            ? `${fmtNum(summary.personas.segment)} ${t('segment')} · ${fmtNum(summary.personas.person_held)} ${t('held person personas')}`
            : t('the campaigns service did not answer')}
        />
        <RailCell
          label={t('Sequences')}
          value={summary ? fmtNum(summary.sequences.total) : t('not collected')}
          tone={!summary ? 'unknown' : summary.sequences.total === 0 ? 'nil' : 'ink'}
          detail={summary
            ? `${fmtNum(summary.sequences.running)} ${t('running')} · ${fmtNum(summary.sequences.draft)} ${t('draft')} · ${fmtNum(summary.enrolments.active)} ${t('active enrolments')}`
            : t('the campaigns service did not answer')}
        />
        <RailCell
          label={t('Drafts waiting')}
          go="→"
          onClick={() => open(ROUTES.review({ status: 'unheld', kind: 'draft' }))}
          value={summary ? fmtNum(summary.drafts_waiting) : t('not collected')}
          tone={!summary ? 'unknown' : summary.drafts_waiting === 0 ? 'nil' : 'attend'}
          detail={topStep
            ? `${topStep.sequence_ref} ${t('step')} ${String(topStep.step_n).padStart(2, '0')} · ${t('held in Review, never sent from here')}`
            : t('nothing waiting for a decision')}
        />
        <RailCell
          label={t('Sent · 7d')}
          value={summary ? fmtNum(summary.sent_7d) : t('not collected')}
          tone={!summary ? 'unknown' : summary.sent_7d === 0 ? 'nil' : 'ink'}
          detail={summary
            ? `${fmtNum(summary.approved_7d)} ${t('approved, waiting in the outbox')}`
            : t('the outbox could not be read')}
        />
        <RailCell
          label={t('Reply rate · 12m')}
          // Counted, and still not sayable: below twenty sends a percentage would be a number
          // that lies. The cell says so in words rather than printing one (§10).
          value={reply && reply.rate !== null ? fmtNum(reply.rate * 100, 0) : t('too few to say')}
          unit={reply && reply.rate !== null ? '%' : undefined}
          tone={!summary ? 'unknown' : reply && reply.rate !== null ? 'ink' : 'unknown'}
          detail={reply
            ? `${fmtNum(reply.n)} ${t('sent')} · ${fmtNum(reply.replies)} ${t('replied')} · ${t('a rate needs {n}', { n: String(reply.min_n) })}`
            : t('the campaigns service did not answer')}
        />
      </Rail>

      {/* ── segment persona (personas agent's component) ─────────────────── */}
      <SegmentPersonaZone onChange={() => { void load(); }} />

      {/* ── sequences ───────────────────────────────────────────────────── */}
      <Zone id="sequences">
        <ZoneHead
          label={t('Sequences')}
          count={sequences ? fmtNum(sequences.length) : undefined}
          note={t('a step drafts; it never sends')}
          endNote={summary
            ? t('{cap} per hour across every campaign · {used} used', { cap: String(summary.hourly.cap), used: String(summary.hourly.used) })
            : undefined}
          route={<button type="button" className="zone-route" onClick={() => setEditorOpen((v) => !v)}>{t('New sequence')}</button>}
        />

        {editorOpen && (
          <Sheet pad style={{ marginBottom: 'var(--s12)' }}>
            <div className="flex gap-8 flex-wrap" style={{ alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: '1 1 320px' }}>
                <label className="form-label" htmlFor="seq-name">{t('Name')}</label>
                <input id="seq-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Autumn 2026 · AI governance')} />
              </div>
              <div className="form-group" style={{ width: 130 }}>
                <label className="form-label" htmlFor="seq-cap">{t('Daily cap')}</label>
                <input id="seq-cap" className="form-input" inputMode="numeric" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} />
              </div>
            </div>
            {steps.map((step, index) => (
              <div key={index} style={{ marginTop: 'var(--s12)' }}>
                <div className="flex gap-8 flex-wrap" style={{ alignItems: 'flex-end' }}>
                  <span className="u-label" style={{ minWidth: 48 }}>{t('Step')} <Fig>{String(index + 1).padStart(2, '0')}</Fig></span>
                  <div className="form-group" style={{ width: 110 }}>
                    <label className="form-label" htmlFor={`step-wait-${index}`}>{t('Wait · days')}</label>
                    <input
                      id={`step-wait-${index}`}
                      className="form-input"
                      inputMode="numeric"
                      value={step.wait_days}
                      onChange={(e) => setSteps(steps.map((s, i) => (i === index ? { ...s, wait_days: e.target.value } : s)))}
                    />
                  </div>
                  <div className="form-group" style={{ flex: '1 1 320px' }}>
                    <label className="form-label" htmlFor={`step-subject-${index}`}>{t('Subject')}</label>
                    <input
                      id={`step-subject-${index}`}
                      className="form-input"
                      value={step.subject}
                      onChange={(e) => setSteps(steps.map((s, i) => (i === index ? { ...s, subject: e.target.value } : s)))}
                    />
                  </div>
                  <label className="form-label" style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={step.end_on_no_reply}
                      onChange={(e) => setSteps(steps.map((s, i) => (i === index ? { ...s, end_on_no_reply: e.target.checked } : s)))}
                    />
                    {t('end here if no reply')}
                  </label>
                  {steps.length > 1 && (
                    <Lnk onClick={() => setSteps(steps.filter((_, i) => i !== index))}>{t('Remove')}</Lnk>
                  )}
                </div>
                <div className="form-group" style={{ marginTop: 'var(--s8)' }}>
                  <label className="form-label" htmlFor={`step-template-${index}`}>
                    {t('Template — {{first_name}}, {{organisation}} and {{role_title}} are filled from the record')}
                  </label>
                  <textarea
                    id={`step-template-${index}`}
                    className="form-input"
                    rows={4}
                    value={step.template_md}
                    onChange={(e) => setSteps(steps.map((s, i) => (i === index ? { ...s, template_md: e.target.value } : s)))}
                  />
                </div>
              </div>
            ))}
            {stepError && <p className="rat" style={{ color: 'var(--err)' }}>{stepError}</p>}
            <div className="flex gap-8 mt-8">
              <button type="button" className="btn btn-sm" onClick={() => setSteps([...steps, emptyStep(6)])}>{t('Add step')}</button>
              <button type="button" className="btn btn-accent btn-sm" disabled={saving || !name.trim()} onClick={() => void save()}>
                {saving ? t('Saving…') : t('Create as draft')}
              </button>
              <Lnk onClick={() => { setEditorOpen(false); setStepError(null); }}>{t('Cancel')}</Lnk>
            </div>
            <p className="rat" style={{ marginTop: 'var(--s8)' }}>
              {t('A new sequence is created as a draft and enrols nobody. Starting it makes its steps due; each due step files a draft proposal under the principal journey:SEQ-…, which you hold in Review and send from the Inbox.')}
            </p>
          </Sheet>
        )}

        {error ? (
          <EmptyState kind="error" title={t('Could not load')} detail={error} onRetry={load} />
        ) : sequences === null ? (
          <Sheet pad><p className="zone-empty">{t('loading…')}</p></Sheet>
        ) : sequences.length === 0 ? (
          <EmptyState
            kind="empty"
            title={t('No sequence yet')}
            detail={t('A sequence is a list of steps with a wait, a subject and a template. It drafts; it never sends.')}
          />
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th style={{ width: 92 }}>{t('Ref')}</th>
                  <th>{t('Name')}</th>
                  <th style={{ width: 90 }}>{t('Status')}</th>
                  <th style={{ width: 70 }} className="num">{t('Steps')}</th>
                  <th style={{ width: 150 }} className="num">{t('Enrolled')}</th>
                  <th style={{ width: 80 }} className="num">{t('Cap')}</th>
                  <th style={{ width: 210 }} />
                </tr>
              </thead>
              <tbody>
                {sequences.map((sequence) => (
                  <tr key={sequence.id} className={selected?.id === sequence.id ? 'row--new' : undefined}>
                    <td><Lnk onClick={() => setSelectedId(sequence.id)}><Ref>{sequence.ref}</Ref></Lnk></td>
                    <td><span className="w">{sequence.name}</span></td>
                    <td><Tag tone={statusTone(sequence.status)}>{t(sequence.status)}</Tag></td>
                    <td className="num"><Fig>{fmtNum(sequence.steps.length)}</Fig></td>
                    <td className="num">
                      <Fig>{fmtNum(sequence.enrolled ?? 0)}</Fig>
                      <span className="d"> · {fmtNum(sequence.active ?? 0)} {t('active')} · {fmtNum(sequence.replied ?? 0)} {t('replied')}</span>
                    </td>
                    <td className="num"><Fig unit={t('/d')}>{fmtNum(sequence.daily_cap)}</Fig></td>
                    <td>
                      <span className="flex gap-8">
                        {sequence.status === 'draft' && (
                          <button type="button" className="btn btn-sm" disabled={busy === `seq-${sequence.id}`} onClick={() => void changeStatus(sequence, 'start')}>{t('Start')}</button>
                        )}
                        {sequence.status === 'running' && (
                          <button type="button" className="btn btn-sm" disabled={busy === `seq-${sequence.id}`} onClick={() => void changeStatus(sequence, 'pause')}>{t('Pause')}</button>
                        )}
                        {sequence.status === 'paused' && (
                          <button type="button" className="btn btn-sm" disabled={busy === `seq-${sequence.id}`} onClick={() => void changeStatus(sequence, 'resume')}>{t('Resume')}</button>
                        )}
                        {sequence.status !== 'ended' && (
                          <Lnk onClick={() => void changeStatus(sequence, 'end')}>{t('End')}</Lnk>
                        )}
                        <Lnk onClick={() => { setSelectedId(sequence.id); setEnrolOpen(true); }}>{t('Enrol')}</Lnk>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── the selected sequence: its steps and its people ──────────────── */}
      {selected && (
        <Zone id="sequence">
          <ZoneHead
            label={<>{t('Sequence')} <Sep /> <Ref>{selected.ref}</Ref> <Sep /> {selected.name}</>}
            count={t('{status} · step {n} of {total}', {
              status: t(selected.status),
              n: String(Math.max(1, (enrolments ?? []).reduce((max, e) => Math.max(max, e.step_n), 0))),
              total: String(selected.steps.length),
            })}
            endNote={summary
              ? t('daily cap {cap} · {suppressed} suppressed · replies thread into the Inbox', {
                cap: String(selected.daily_cap), suppressed: String(summary.enrolments.suppressed),
              })
              : undefined}
          />
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th style={{ width: 48 }} className="num">{t('Step')}</th>
                  <th>{t('Subject and template')}</th>
                  <th style={{ width: 190 }}>{t('Drafts')}</th>
                  <th style={{ width: 110 }}>{t('Branch')}</th>
                </tr>
              </thead>
              <tbody>
                {selected.steps.map((step) => {
                  const waiting = (summary?.drafts_by_step ?? [])
                    .filter((row) => row.sequence_ref === selected.ref && row.step_n === step.n)
                    .reduce((sum, row) => sum + row.count, 0);
                  return (
                    <tr key={step.n}>
                      <td className="num"><Fig>{String(step.n).padStart(2, '0')}</Fig></td>
                      <td>
                        <span className="w">{step.subject}</span>
                        <div className="d">{t('mail')} · {t('day')} <Fig>{fmtNum(step.wait_days)}</Fig></div>
                        <Record className="rat">{step.template_md.slice(0, 180)}{step.template_md.length > 180 ? '…' : ''}</Record>
                      </td>
                      <td>
                        {waiting > 0
                          ? <><Fig>{fmtNum(waiting)}</Fig> <span className="d">{t('waiting in Review')}</span></>
                          : <span className="d n-none">—</span>}
                      </td>
                      <td>
                        {step.branches.length
                          ? step.branches.map((branch) => (
                            <Tag key={branch.on} tone="ghost">{t(branch.on)} → {branch.goto === 'end' ? t('end') : String(branch.goto)}</Tag>
                          ))
                          : <span className="d">{t('reply stops it')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tbl>
          </Sheet>

          {enrolOpen && (
            <Sheet pad style={{ marginTop: 'var(--s12)' }}>
              <div className="flex gap-8 flex-wrap" style={{ alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: '1 1 320px' }}>
                  <label className="form-label" htmlFor="enrol-q">{t('Find people to enrol')}</label>
                  <input
                    id="enrol-q"
                    className="form-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
                    placeholder={t('name, organisation…')}
                  />
                </div>
                <button type="button" className="btn btn-sm" onClick={() => void search()}>{t('Search')}</button>
                <button type="button" className="btn btn-accent btn-sm" disabled={!picked.length || busy === 'enrol'} onClick={() => void doEnrol()}>
                  {t('Enrol {n}', { n: String(picked.length) })}
                </button>
                <Lnk onClick={() => { setEnrolOpen(false); setPicked([]); }}>{t('Cancel')}</Lnk>
              </div>
              {matches === null ? null : matches.length === 0 ? (
                <p className="zone-empty">{t('Nobody matches.')}</p>
              ) : (
                <div className="chips" style={{ marginTop: 'var(--s8)' }}>
                  {matches.map((person) => (
                    <button
                      key={person.id}
                      type="button"
                      className={`chip${picked.includes(person.id) ? ' active' : ''}`}
                      aria-pressed={picked.includes(person.id)}
                      onClick={() => setPicked(picked.includes(person.id) ? picked.filter((id) => id !== person.id) : [...picked, person.id])}
                    >
                      {person.name} <Ref>{person.ref}</Ref>
                    </button>
                  ))}
                </div>
              )}
              <p className="rat" style={{ marginTop: 'var(--s8)' }}>
                {t('Do-not-contact, an open deal at negotiation or later, a reply already received and a risk signal in the last 14 days each suppress an enrolment with a reason you can read below.')}
              </p>
            </Sheet>
          )}

          <Sheet clip style={{ marginTop: 'var(--s12)' }}>
            {enrolments === null ? (
              <p className="zone-empty">{t('The enrolments could not be read.')}</p>
            ) : enrolments.length === 0 ? (
              <p className="zone-empty">{t('Nobody is enrolled. Enrol people; a sequence with no enrolment drafts nothing.')}</p>
            ) : (
              <Tbl>
                <thead>
                  <tr>
                    <th style={{ width: 220 }}>{t('Person')}</th>
                    <th style={{ width: 90 }}>{t('Status')}</th>
                    <th style={{ width: 60 }} className="num">{t('Step')}</th>
                    <th style={{ width: 150 }}>{t('Next')}</th>
                    <th>{t('Why it stopped')}</th>
                    <th style={{ width: 110 }} />
                  </tr>
                </thead>
                <tbody>
                  {enrolments.map((enrolment) => (
                    <tr key={enrolment.id}>
                      <td>
                        <Lnk onClick={() => open(ROUTES.person({ id: enrolment.person_id }))}>{enrolment.person_name}</Lnk>{' '}
                        <Ref>{enrolment.person_ref ?? ''}</Ref>
                      </td>
                      <td><Tag tone={enrolmentTone(enrolment.status)}>{t(enrolment.status)}</Tag></td>
                      <td className="num"><Fig>{fmtNum(enrolment.step_n)}</Fig></td>
                      <td>{enrolment.next_at ? <Fig>{fmtDateTime(enrolment.next_at)}</Fig> : <span className="d n-none">—</span>}</td>
                      <td>
                        {enrolment.suppressed_reason
                          ? <span className="d">{enrolment.suppressed_reason}</span>
                          : <span className="d n-none">—</span>}
                      </td>
                      <td>
                        {enrolment.status === 'active' || enrolment.status === 'paused' ? (
                          <Lnk onClick={() => void stopEnrolment(enrolment.id).then(loadEnrolments)}>{t('Stop')}</Lnk>
                        ) : enrolment.status === 'suppressed' || enrolment.status === 'stopped' ? (
                          <Lnk onClick={() => void resumeEnrolment(enrolment.id).then(loadEnrolments)}>{t('Resume')}</Lnk>
                        ) : <span className="d n-none">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tbl>
            )}
          </Sheet>
        </Zone>
      )}

      {/* ── drafts to approve ───────────────────────────────────────────── */}
      <Zone id="drafts">
        <ZoneHead
          label={t('Drafts to approve')}
          count={summary ? fmtNum(summary.drafts_waiting) : undefined}
          note={t('each one names the sequence and the step that asked for it')}
          route={
            <button type="button" className="zone-route" onClick={() => open(ROUTES.review({ status: 'unheld', kind: 'draft' }))}>
              {t('Review →')}
            </button>
          }
        />
        {!summary ? (
          <Sheet pad><p className="zone-empty">{t('The campaigns service did not answer, so nothing here was counted.')}</p></Sheet>
        ) : summary.drafts_by_step.length === 0 ? (
          <Sheet pad>
            <p className="zone-empty">
              {t('No draft is waiting. A running sequence files one per due step, inside working hours, up to its daily cap.')}
            </p>
          </Sheet>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>{t('Sequence')}</th>
                  <th style={{ width: 80 }} className="num">{t('Step')}</th>
                  <th style={{ width: 100 }} className="num">{t('Waiting')}</th>
                  <th>{t('Where it is decided')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.drafts_by_step.map((row) => (
                  <tr key={`${row.sequence_ref}-${row.step_n}`}>
                    <td><Ref>{row.sequence_ref}</Ref></td>
                    <td className="num"><Fig>{String(row.step_n).padStart(2, '0')}</Fig></td>
                    <td className="num"><Fig>{fmtNum(row.count)}</Fig></td>
                    <td>
                      <Att kind="proposed" />{' '}
                      <Lnk onClick={() => open(ROUTES.review({ status: 'unheld', principal: `journey:${row.sequence_ref}` }))}>
                        {t('Review the drafts from {ref}', { ref: row.sequence_ref })}
                      </Lnk>
                      <span className="d"> · {t('holding one writes it to the outbox; sending is a second click in the Inbox')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── attribution ─────────────────────────────────────────────────── */}
      <Zone id="attribution">
        <ZoneHead
          label={t('Attribution · 12m')}
          note={t('a rate is shown only above 20 assignments')}
        />
        {experiments === null ? (
          <Sheet pad><p className="zone-empty">{t('The experiments could not be read.')}</p></Sheet>
        ) : experiments.length === 0 ? (
          <Sheet pad>
            <p className="zone-empty">
              {t('No experiment is running. An experiment assigns its variants round-robin to the drafts of one sequence and reads the outcome off replies and meetings.')}
            </p>
          </Sheet>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Experiment')}</th>
                  <th style={{ width: 90 }}>{t('Variable')}</th>
                  <th style={{ width: 120 }}>{t('Variant')}</th>
                  <th style={{ width: 70 }} className="num">{t('n')}</th>
                  <th style={{ width: 90 }} className="num">{t('Replies')}</th>
                  <th style={{ width: 90 }} className="num">{t('Meetings')}</th>
                  <th style={{ width: 150 }} className="num">{t('Reply rate')}</th>
                </tr>
              </thead>
              <tbody>
                {experiments.flatMap((experiment) => {
                  const rows = reports[experiment.id]?.variants ?? experiment.variants.map((v) => ({
                    variant: v.key, n: 0, replies: 0, meetings: 0, reply_rate: null, meeting_rate: null,
                  }));
                  return rows.map((row, index) => (
                    <tr key={`${experiment.id}-${row.variant}`}>
                      {index === 0 ? <td rowSpan={rows.length}><span className="w">{experiment.name}</span>{experiment.ended_at && <div className="d">{t('ended')} <Fig>{fmtDate(experiment.ended_at)}</Fig></div>}</td> : null}
                      {index === 0 ? <td rowSpan={rows.length}><Tag>{t(experiment.variable)}</Tag></td> : null}
                      <td>{row.variant}</td>
                      <td className="num"><Fig>{fmtNum(row.n)}</Fig></td>
                      <td className="num"><Fig>{fmtNum(row.replies)}</Fig></td>
                      <td className="num"><Fig>{fmtNum(row.meetings)}</Fig></td>
                      <td className="num">
                        {row.reply_rate === null
                          ? <span className="d n-unknown">{t('too few to say')}</span>
                          : <Fig unit="%">{fmtNum(row.reply_rate * 100, 0)}</Fig>}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>
    </PageShell>
  );
}
